import { getAccessToken } from 'utils/auth';
import { GeofenceOutcome, runLoginGeofenceCheck } from 'utils/geofenceVerify';
import { PLAYER_API_URL } from 'utils/urls';

/**
 * Login-time geolocation gate, vendor half plus server half.
 *
 * The browser gets a verdict from LexisNexis / Radar; the SERVER decides what
 * it means. That split is the whole point: the vendor's own `passed` flag is
 * read from a page the player controls, so it is a hint. The signed token is
 * the evidence, and only the server holds the key to check it.
 */

export type GeofenceDecision = {
  /** Whether the player may proceed. Authoritative: it comes from the server. */
  allowed: boolean;
  /** Sanitized server reason, for logging and copy selection. */
  reason: string;
};

/**
 * Fail-open default used when we cannot reach our own backend.
 *
 * Deliberate, and narrow: this fires only when OUR endpoint is unreachable, not
 * when the vendor declines. An outage in our own infrastructure locking every
 * player out of the product is a worse failure than briefly admitting someone
 * we would otherwise have questioned, and the server still gates the paths that
 * actually matter. If compliance rules that geofencing must be hard-fail, this
 * constant is the one line to change.
 */
const BACKEND_UNREACHABLE: GeofenceDecision = { allowed: true, reason: 'check_unavailable' };

/**
 * Whether a vendor lookup is owed, or an existing verdict still covers us.
 *
 * `decision` is present only when no check is needed, and it is the server's
 * answer to the question this check would have asked.
 */
export type GeofenceCheckStatus =
  | { required: true }
  | { required: false; decision: GeofenceDecision };

/** Asked for whenever we cannot get a usable answer out of the status probe. */
const CHECK_REQUIRED: GeofenceCheckStatus = { required: true };

/**
 * Ask the server whether a vendor lookup is actually owed.
 *
 * PURELY A COST CONTROL. A Radar lookup is billed per call, and the server
 * already treats a stored verdict as current for 24 hours -- `$connect` admits
 * from it all day without re-verifying. Buying a fresh one on every app open
 * paid for an answer the server was going to ignore. Measured against
 * production on 2026-10-05, roughly two thirds of lookups were redundant.
 *
 * Nothing here decides admission. The verdict the server enforces on is written
 * only by POST /location/verify from a vendor-signed token, so skipping a call
 * skips a purchase and nothing else.
 *
 * Fails toward spending: any error, any unreadable shape, and we do the check.
 * One wasted lookup costs money; the other direction would skip a check that
 * was owed.
 */
export async function getLocationCheckStatus(): Promise<GeofenceCheckStatus> {
  try {
    const response = await fetch(`${PLAYER_API_URL}/location/status`, {
      method: 'GET',
      headers: { Accept: 'application/json', Authorization: `Bearer ${getAccessToken()}` },
      cache: 'no-store'
    });

    if (!response.ok) {
      console.warn(`[geofence] /location/status returned ${response.status}`);
      return CHECK_REQUIRED;
    }

    const body = await response.json();
    // Anything that is not an explicit `false` means run the check, so a
    // truncated or unexpected body cannot silently skip one.
    if (body?.required !== false) return CHECK_REQUIRED;

    return {
      required: false,
      decision: {
        allowed: body?.allowed === true,
        reason: typeof body?.reason === 'string' ? body.reason : 'unknown'
      }
    };
  } catch (err) {
    console.warn('[geofence] /location/status unreachable:', err);
    return CHECK_REQUIRED;
  }
}

/**
 * Run the vendor check, then post the result for a server decision.
 *
 * Note that an `unavailable` vendor outcome is still POSTed, with a null token.
 * We do not quietly skip the server call: an account carrying the
 * isRestrictedFlag bypass must be admitted even when the vendor never answers,
 * and only the server knows about that flag.
 */
export async function verifyLoginLocation(
  playerGUID: string,
  options: { shadow?: boolean } = {}
): Promise<GeofenceDecision> {
  const outcome: GeofenceOutcome = await runLoginGeofenceCheck(playerGUID);
  const token = outcome.status === 'verdict' ? outcome.token : null;

  try {
    const response = await fetch(`${PLAYER_API_URL}/location/verify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${getAccessToken()}`
      },
      cache: 'no-store',
      body: JSON.stringify({
        token,
        // Row key for the player_devices compliance record, NOT location data.
        // Client-asserted on purpose: it selects which device row to update, so
        // trusting it cannot let anyone claim a position they were not at --
        // the coordinates come from the vendor-signed token.
        //
        // Must be localStorage 'deviceID', the same source the legacy
        // trackLocation path used. getDeviceId() reads the `_socketuuid`
        // cookie, which is a DIFFERENT identifier: using it would match no
        // existing player_devices row and the update would silently write
        // nothing.
        deviceID: localStorage.getItem('deviceID') ?? '',
        // Diagnostics only. The server must never make its decision from these:
        // they are client-asserted and therefore forgeable. They exist so the
        // observe-mode logs can tell "vendor said no" apart from "vendor never
        // answered", which is what decides whether this vendor can cover
        // desktop web at all.
        clientStatus: outcome.status,
        clientCode: outcome.status === 'unavailable' ? outcome.code : null,
        // Observation run: legacy decided what this player sees and nothing
        // acts on the verdict this request produces. It only changes the audit
        // line's `source`, so a refusal that was never enforced is not counted
        // later as one that was. Client-asserted like the two fields above, and
        // safe for the same reason: it affects a log label, never a decision.
        shadow: options.shadow === true
      })
    });

    if (!response.ok) {
      console.warn(`[geofence] /location/verify returned ${response.status}`);
      return BACKEND_UNREACHABLE;
    }

    const body = await response.json();
    return {
      // Treat anything that is not an explicit `true` as a rejection, so a
      // malformed or truncated response can never read as approval.
      allowed: body?.allowed === true,
      reason: typeof body?.reason === 'string' ? body.reason : 'unknown'
    };
  } catch (err) {
    console.warn('[geofence] /location/verify unreachable:', err);
    return BACKEND_UNREACHABLE;
  }
}
